// Lightweight LiveKit observer client (ESM)
import * as LK from 'livekit-client';

let room = null;
let isConnecting = false;
let dotNetRef = null; 

const log = (...a) => console.log('[LK-OBS]', ...a);
const warn = (...a) => console.warn('[LK-OBS]', ...a);
const err = (...a) => console.error('[LK-OBS]', ...a);

function notifyDotNetConnected(connected) {
    if (!dotNetRef) return;
    try {
        dotNetRef.invokeMethodAsync('OnLiveKitConnectionChanged', !!connected);
    } catch (e) {
        warn('dotnet notify failed', e);
    }
}

export function lk_isConnected() {
    return !!room && room.state === LK.ConnectionState.Connected;
}

function wireRoom(r) {
    r
        .on(LK.RoomEvent.Connected, () => {
            log('🔗 Connected');
            notifyDotNetConnected(true);
        })
        .on(LK.RoomEvent.Disconnected, (reason) => {
            log('🔌 Disconnected', reason);
            notifyDotNetConnected(false);
            dotNetRef = null;
        })
        .on(LK.RoomEvent.Reconnecting, () => log('… Reconnecting'))
        .on(LK.RoomEvent.Reconnected, () => {
            log('✅ Reconnected');
            notifyDotNetConnected(true);
        })
        .on(LK.RoomEvent.TranscriptionReceived, (segments, participant) => {
            const who = participant?.identity ?? 'unknown';
            try {
                segments.forEach(seg => {
                    const isFinal = (seg && typeof seg.final === 'boolean') ? seg.final : false;
                    const text = seg?.text ?? '';
                    
                    if (!text) return;
                    
                    if(dotNetRef) {
                        dotNetRef.invokeMethodAsync('OnTranscriptionReceived', text, isFinal, who)
                            .catch(e => warn('OnTranscriptionReceived failed', e));
                    }

                    const tag = isFinal ? '📝 FINAL' : '⌨️ INTERIM';
                    log(`${tag} transcription from ${who}:`, text);
                });
            } catch (e) {
                warn('TranscriptionReceived handler error:', e);
            }
        })
        // Data channel: specifically for 'lk.chat' messages
        .on(LK.RoomEvent.DataReceived, (payload, participant, kind, topic) => {
            if (topic !== 'lk.chat') return;

            let text = '';
            try { 
                text = new TextDecoder().decode(payload); 
            } catch (e) { 
                warn('lk.chat decode failed', e); 
                return; 
            }

            // Identify the sender. If it's the local user or a user participant, we label 'user'.
            // If participant is null, we assume 'user' for data messages in this context.
            const identity = participant?.identity || 'user';
            
            // Note: We treat chat messages as FINAL transcriptions
            const isFinal = true;
            
            log(`💬 Chat received from ${identity}:`, text);

            if (dotNetRef && text) {
                dotNetRef.invokeMethodAsync('OnTranscriptionReceived', text, isFinal, identity)
                    .catch(e => warn('.NET OnTranscriptionReceived (lk.chat) failed', e));
            }
        });
}

export async function lk_connect(url, token, opts = {}) {
    if (room) {
        warn('already connected; call lk_disconnect() first');
        return true;
    }

    dotNetRef = opts.dotNetRef || null;
    
    try {
        isConnecting = true;
        log('🚀 connecting…', { url });

        room = new LK.Room();
        wireRoom(room);

        await room.connect(url, token);
        log('✅ connected to room', { sid: room.sid });
        notifyDotNetConnected(true);

        isConnecting = false;
        return true;
    } catch (e) {
        isConnecting = false;
        err('connect failed', e);
        notifyDotNetConnected(false);
        try { await lk_disconnect(); } catch {}
        return false;
    }
}

export async function lk_disconnect() {
    try {
        if (room) {
            log('🔌 disconnecting…');
            await room.disconnect();
        }
    } catch (e) {
        warn('disconnect error (ignored)', e);
    } finally {
        room = null;
        isConnecting = false;
        notifyDotNetConnected(false);
        dotNetRef = null;
    }
}

if (typeof window !== 'undefined') {
    window.LKObserver = {
        lk_connect,
        lk_disconnect,
        lk_isConnected,
    };
}